import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import {
  AuthErrorCode,
  signupRequest,
  type ReferralResolveResponse,
  type SubjectType,
} from '@forbiz/contracts'
import { api, ApiError, useAuth } from '@forbiz/web-kit'

// 초대 수락 화면과 같은 인증 카드 스타일을 재사용한다(디자인은 추후 BIZ 디자인 트랙).
import styles from './InviteAcceptPage.module.css'

/**
 * 셀프 회원가입(BIZ-67, 공개 경로 /signup).
 *
 * 초대 없이 주체(기업·단체·개인·사모임·기타)를 골라 계정을 만든다. 성공하면 서버가
 * 계정을 활성화하고 세션 쿠키를 심으며, useAuth.signup이 응답 user로 컨텍스트를 채워
 * 자동 로그인된다(초대 수락과 동일 원리).
 *
 * 기업 가승인·검증(사업자등록증·재직증명서)은 BIZ-68에서 이 위에 얹는다 —
 * 여기서는 기업이면 사업자등록번호만 받는다.
 */

const SUBJECT_OPTIONS: { value: SubjectType; label: string }[] = [
  { value: 'INDIVIDUAL', label: '개인' },
  { value: 'COMPANY', label: '기업' },
  { value: 'ORG', label: '단체' },
  { value: 'GROUP', label: '사모임' },
  { value: 'ETC', label: '기타' },
]

/** 주체에 따라 이름 칸 라벨이 달라진다(개인은 본인 성명). */
function orgNameLabel(s: SubjectType): string {
  if (s === 'INDIVIDUAL') return '이름'
  if (s === 'COMPANY') return '상호 (회사명)'
  return '단체명'
}

export function SignupPage() {
  const navigate = useNavigate()
  const { signup } = useAuth()

  // 추천 링크로 들어오면 ?ref=CODE가 붙는다(BIZ-75). 화면엔 안내만, 검증·귀속은 서버가 한다.
  const [searchParams] = useSearchParams()
  const referralCode = (searchParams.get('ref') ?? '').trim()

  // 초대자 표시(BIZ-132, 열린 질문 §1): 코드를 서버에서 해석해 "누가 초대했는지"를 보여주고,
  // 무효·만료 코드는 정직하게 알린다(예전엔 무효 코드도 "적용됨"으로 과장 표기).
  const [inviter, setInviter] = useState<ReferralResolveResponse | null>(null)
  useEffect(() => {
    if (!referralCode) return
    let alive = true
    api
      .get<ReferralResolveResponse>(`/api/referral/resolve?code=${encodeURIComponent(referralCode)}`)
      .then((r) => {
        if (alive) setInviter(r)
      })
      .catch(() => {
        if (alive) setInviter({ valid: false, inviterName: null })
      })
    return () => {
      alive = false
    }
  }, [referralCode])

  const [subjectType, setSubjectType] = useState<SubjectType>('INDIVIDUAL')
  const [orgName, setOrgName] = useState('')
  const [contactName, setContactName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [bizRegNo, setBizRegNo] = useState('')
  const [ceoName, setCeoName] = useState('')
  const [bizOpenDate, setBizOpenDate] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const isIndividual = subjectType === 'INDIVIDUAL'
  const isCompany = subjectType === 'COMPANY'

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (submitting) return // 이중 제출 재진입 가드
    setFieldErrors({})
    setFormError('')

    // 개인은 담당자 이름을 따로 받지 않는다(orgName=본인). 기업 외에는 사업자번호를 보내지 않는다.
    const payload = {
      subjectType,
      orgName,
      contactName: !isIndividual && contactName ? contactName : undefined,
      email,
      password,
      phone: phone || undefined,
      bizRegNo: isCompany && bizRegNo ? bizRegNo : undefined,
      ceoName: isCompany && ceoName ? ceoName : undefined,
      bizOpenDate: isCompany && bizOpenDate ? bizOpenDate : undefined,
      referralCode: referralCode || undefined,
    }

    const parsed = signupRequest.safeParse(payload)
    const errs: Record<string, string> = {}
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0])
        if (key && !errs[key]) errs[key] = issue.message
      }
    }
    // 비밀번호 확인 불일치는 클라에서만 막는다(서버로 보내지 않는다).
    if (password !== confirm) errs['confirm'] = '비밀번호가 일치하지 않습니다'
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs)
      return
    }
    // 여기 도달 = 파싱 성공(실패면 위 errs에서 걸림). parsed.data를 좁히기 위한 가드.
    if (!parsed.success) return

    setSubmitting(true)
    try {
      // signup이 세션 쿠키를 심고 응답 user로 컨텍스트를 채운다(요청 1회) → 자동 로그인.
      await signup(parsed.data)
      navigate('/', { replace: true })
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === AuthErrorCode.DUPLICATE_EMAIL) {
          setFieldErrors({ email: '이미 가입된 이메일입니다' })
        } else if (err.code === AuthErrorCode.DUPLICATE_BIZ_REG_NO) {
          setFieldErrors({ bizRegNo: '이미 등록된 사업자등록번호입니다' })
        } else if (err.code === AuthErrorCode.VALIDATION_FAILED && err.fields) {
          setFieldErrors(err.fields)
        } else {
          setFormError(err.message)
        }
      } else {
        setFormError('일시적인 오류가 발생했습니다. 잠시 후 다시 시도하세요')
      }
      setSubmitting(false)
    }
  }

  return (
    <div className={styles.page}>
      <form className={styles.card} onSubmit={onSubmit} noValidate>
        <h1 className={styles.brand}>포비즈</h1>
        <p className={styles.subtitle}>회원가입</p>

        {referralCode && (
          <div className={styles.hint} role="status">
            {inviter == null ? (
              <>추천 코드 확인 중…</>
            ) : inviter.valid ? (
              <span className={styles.hintRow}>
                <span className={styles.hintIcon} aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none">
                    <rect x="4" y="10" width="16" height="10.5" rx="1.5" stroke="currentColor" strokeWidth="1.7" />
                    <path d="M4 13.2h16M12 10v10.5" stroke="currentColor" strokeWidth="1.7" />
                    <path d="M12 10c-1-2.8-5-2.4-5 0h5zM12 10c1-2.8 5-2.4 5 0h-5z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                  </svg>
                </span>
                <span>
                  <b>{inviter.inviterName}</b>의 초대로 가입해요 — 가입하면 추천인과 연결됩니다.
                </span>
              </span>
            ) : (
              <>확인되지 않는 추천 코드예요 — 가입은 코드 없이 그대로 진행돼요.</>
            )}
          </div>
        )}

        {formError && (
          <div className={styles.formError} role="alert">
            {formError}
          </div>
        )}

        <div className={styles.field}>
          <label className={styles.label} htmlFor="subjectType">
            가입 유형
          </label>
          <select
            id="subjectType"
            value={subjectType}
            onChange={(e) => setSubjectType(e.target.value as SubjectType)}
            disabled={submitting}
          >
            {SUBJECT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="orgName">
            {orgNameLabel(subjectType)}
          </label>
          <input
            id="orgName"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            aria-invalid={fieldErrors['orgName'] ? true : undefined}
            disabled={submitting}
          />
          {fieldErrors['orgName'] && <p className={styles.fieldError}>{fieldErrors['orgName']}</p>}
        </div>

        {!isIndividual && (
          <div className={styles.field}>
            <label className={styles.label} htmlFor="contactName">
              담당자 이름
            </label>
            <input
              id="contactName"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              aria-invalid={fieldErrors['contactName'] ? true : undefined}
              disabled={submitting}
            />
            {fieldErrors['contactName'] && (
              <p className={styles.fieldError}>{fieldErrors['contactName']}</p>
            )}
          </div>
        )}

        {isCompany && (
          <div className={styles.field}>
            <label className={styles.label} htmlFor="bizRegNo">
              사업자등록번호
            </label>
            <input
              id="bizRegNo"
              inputMode="numeric"
              placeholder="000-00-00000"
              value={bizRegNo}
              onChange={(e) => setBizRegNo(e.target.value)}
              aria-invalid={fieldErrors['bizRegNo'] ? true : undefined}
              disabled={submitting}
            />
            {fieldErrors['bizRegNo'] && (
              <p className={styles.fieldError}>{fieldErrors['bizRegNo']}</p>
            )}
          </div>
        )}

        {isCompany && (
          <div className={styles.field}>
            <label className={styles.label} htmlFor="ceoName">
              대표자명
            </label>
            <input
              id="ceoName"
              value={ceoName}
              onChange={(e) => setCeoName(e.target.value)}
              aria-invalid={fieldErrors['ceoName'] ? true : undefined}
              disabled={submitting}
            />
            {fieldErrors['ceoName'] ? (
              <p className={styles.fieldError}>{fieldErrors['ceoName']}</p>
            ) : (
              <p className={styles.hint}>사업자등록증의 대표자와 동일하게 — 국세청 확인에 쓰여요</p>
            )}
          </div>
        )}

        {isCompany && (
          <div className={styles.field}>
            <label className={styles.label} htmlFor="bizOpenDate">
              개업일자
            </label>
            <input
              id="bizOpenDate"
              type="date"
              value={bizOpenDate}
              onChange={(e) => setBizOpenDate(e.target.value)}
              aria-invalid={fieldErrors['bizOpenDate'] ? true : undefined}
              disabled={submitting}
            />
            {fieldErrors['bizOpenDate'] ? (
              <p className={styles.fieldError}>{fieldErrors['bizOpenDate']}</p>
            ) : (
              <p className={styles.hint}>사업자등록증의 개업연월일 — 국세청 확인에 쓰여요</p>
            )}
          </div>
        )}

        <div className={styles.field}>
          <label className={styles.label} htmlFor="email">
            이메일
          </label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={fieldErrors['email'] ? true : undefined}
            disabled={submitting}
          />
          {fieldErrors['email'] && <p className={styles.fieldError}>{fieldErrors['email']}</p>}
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="phone">
            휴대폰 (선택 · 주문 알림을 받아요)
          </label>
          <input
            id="phone"
            inputMode="numeric"
            autoComplete="tel"
            placeholder="010-0000-0000"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            aria-invalid={fieldErrors['phone'] ? true : undefined}
            disabled={submitting}
          />
          {fieldErrors['phone'] && <p className={styles.fieldError}>{fieldErrors['phone']}</p>}
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="password">
            비밀번호
          </label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-invalid={fieldErrors['password'] ? true : undefined}
            disabled={submitting}
          />
          {fieldErrors['password'] ? (
            <p className={styles.fieldError}>{fieldErrors['password']}</p>
          ) : (
            <p className={styles.hint}>10자 이상, 영문·숫자·기호 중 2종 이상</p>
          )}
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="confirm">
            비밀번호 확인
          </label>
          <input
            id="confirm"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            aria-invalid={fieldErrors['confirm'] ? true : undefined}
            disabled={submitting}
          />
          {fieldErrors['confirm'] && <p className={styles.fieldError}>{fieldErrors['confirm']}</p>}
        </div>

        <button type="submit" data-variant="primary" className={styles.submit} disabled={submitting}>
          {submitting ? '가입 중…' : '가입하고 시작하기'}
        </button>

        <div className={styles.footer}>
          <Link to="/login">이미 계정이 있으신가요? 로그인</Link>
        </div>
      </form>
    </div>
  )
}
